import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Box, Typography, IconButton, Slider } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import SkipNextIcon from '@mui/icons-material/SkipNext'
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import VolumeDownIcon from '@mui/icons-material/VolumeDown'
import VolumeMuteIcon from '@mui/icons-material/VolumeMute'
import MusicNoteIcon from '@mui/icons-material/MusicNote'
import { getEngagementMusicUrl, MUSIC_URL_ERROR_CODES } from 'src/consumer/services/featureServices'

// Lazy load hls.js to avoid adding it to main bundle
let Hls = null
const loadHls = async () => {
  if (!Hls && typeof window !== 'undefined') {
    try {
      const hlsModule = await import('hls.js')
      // hls.js exports as default in most cases
      Hls = hlsModule.default || hlsModule
    } catch (error) {
      console.error('Failed to load hls.js:', error)
    }
  }
  return Hls
}

// Default tracks if none provided (using placeholder)
const DEFAULT_TRACKS = [
  { id: 'default-1', name: 'Calm Waters', mediaId: null },
  { id: 'default-2', name: 'Forest Breeze', mediaId: null },
  { id: 'default-3', name: 'Mountain Peace', mediaId: null },
]

/**
 * SoothingMusicPlayer - Plays background music while waiting for session
 * 
 * @param {Array} tracks - Array of track objects with { id, name, mediaId }
 * @param {string} sessionSlug - Public session slug (for /v/* routes)
 * @param {string} bookedEventId - Booked event ID (for /l/* routes)
 * @param {string} shortKey - Optional session key/OTP
 */
export default function SoothingMusicPlayer({ 
  tracks = DEFAULT_TRACKS,
  sessionSlug,
  bookedEventId,
  shortKey,
}) {
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(0.5)
  const [isMuted, setIsMuted] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [trackUrl, setTrackUrl] = useState(null)
  const audioRef = useRef(null)
  const hlsRef = useRef(null)
  
  // Cache for fetched signed URLs to avoid re-fetching
  const signedUrlCacheRef = useRef({})
  // Retry state
  const retryTimeoutRef = useRef(null)
  const retryCountRef = useRef(0)
  const MAX_RETRIES = 3

  const currentTrack = tracks[currentTrackIndex] || DEFAULT_TRACKS[0]

  // Cleanup retry timeout on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
      }
    }
  }, [])

  // Fetch signed URL for a track with error handling
  const fetchSignedUrl = useCallback(async (mediaId, isRetry = false) => {
    if (!mediaId) return { success: false, error: 'No mediaId' }
    
    // Check cache first (skip for retries to get fresh URL)
    if (!isRetry && signedUrlCacheRef.current[mediaId]) {
      return { success: true, signedUrl: signedUrlCacheRef.current[mediaId] }
    }
    
    try {
      const result = await getEngagementMusicUrl({
        slug: sessionSlug,
        bookedEventId,
        shortKey,
        mediaId,
      })
      
      if (result?.success && result?.signedUrl) {
        // Cache the URL
        signedUrlCacheRef.current[mediaId] = result.signedUrl
        retryCountRef.current = 0 // Reset retry count on success
        return { success: true, signedUrl: result.signedUrl }
      }
      
      // Handle specific error codes
      if (result?.isSessionEnded) {
        return { success: false, error: 'Session has ended', permanent: true }
      }
      
      if (result?.isPermissionError) {
        return { success: false, error: 'Permission denied', permanent: true }
      }
      
      if (result?.shouldRefreshJoin) {
        return { success: false, error: 'Track unavailable', permanent: true }
      }
      
      if (result?.canRetry && retryCountRef.current < MAX_RETRIES) {
        return { 
          success: false, 
          error: result.error || 'Temporary error',
          canRetry: true,
          retryAfterMs: result.retryAfterMs || 5000,
        }
      }
      
      return { success: false, error: result?.error || 'Failed to load audio' }
    } catch (error) {
      console.error('Error fetching music URL:', error)
      if (retryCountRef.current < MAX_RETRIES) {
        return { success: false, error: 'Network error', canRetry: true, retryAfterMs: 5000 }
      }
      return { success: false, error: 'Network error' }
    }
  }, [sessionSlug, bookedEventId, shortKey])

  // Load track URL when track changes
  useEffect(() => {
    // Clear any pending retry
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }
    retryCountRef.current = 0
    
    const loadTrackUrl = async (isRetry = false) => {
      if (!isRetry) {
        setTrackUrl(null)
        setHasError(false)
        setErrorMessage('')
      }
      
      if (!currentTrack?.mediaId) {
        setHasError(true)
        setErrorMessage('No audio available')
        return
      }
      
      setIsLoading(true)
      const result = await fetchSignedUrl(currentTrack.mediaId, isRetry)
      setIsLoading(false)
      
      if (result?.success && result?.signedUrl) {
        setTrackUrl(result.signedUrl)
        setHasError(false)
        setErrorMessage('')
      } else if (result?.canRetry && !result?.permanent) {
        // Schedule a retry
        retryCountRef.current += 1
        const retryDelay = result.retryAfterMs || (5000 * retryCountRef.current) // Exponential backoff
        setErrorMessage(`Loading... (retry ${retryCountRef.current})`)
        
        retryTimeoutRef.current = setTimeout(() => {
          loadTrackUrl(true)
        }, retryDelay)
      } else {
        setHasError(true)
        setErrorMessage(result?.error || 'Unable to load audio')
      }
    }
    
    loadTrackUrl()
  }, [currentTrackIndex, currentTrack?.mediaId, fetchSignedUrl])

  // Get token for HLS authentication
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null

  // Initialize HLS player when URL changes
  useEffect(() => {
    if (!trackUrl || !audioRef.current) return

    const initHls = async () => {
      const HlsClass = await loadHls()
      if (!HlsClass) {
        setHasError(true)
        setErrorMessage('HLS player not available')
        return
      }

      // Clean up previous HLS instance
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }

      // Check if HLS is supported
      if (HlsClass.isSupported()) {
        const hls = new HlsClass({
          debug: false,
          xhrSetup: (xhr, url) => {
            const isKeyRequest = url?.includes('/hls/key')
            // Add Authorization header for authenticated HLS streams
            if (token) {
              xhr.setRequestHeader('Authorization', `Bearer ${token}`)
            }
            
            // For public routes without token, add session slug as header for key requests
            // This allows the backend to authenticate based on session slug
            if (isKeyRequest && !token && sessionSlug) {
              xhr.setRequestHeader('X-Session-Slug', sessionSlug)
              if (shortKey) {
                xhr.setRequestHeader('X-Short-Key', shortKey)
              }
            }
          },
        })

        hls.loadSource(trackUrl)
        hls.attachMedia(audioRef.current)

        // Intercept fragment parsing to modify key URLs for public routes (earlier in the pipeline)
        hls.on(HlsClass.Events.FRAG_PARSING_DATA, (event, data) => {
          if (!token && sessionSlug && data?.frag?.decryptdata?.uri && data.frag.decryptdata.uri.includes('/hls/key')) {
            try {
              const keyUrl = new URL(data.frag.decryptdata.uri)
              keyUrl.searchParams.set('slug', sessionSlug)
              if (shortKey) {
                keyUrl.searchParams.set('shortKey', shortKey)
              }
              data.frag.decryptdata.uri = keyUrl.toString()
            } catch (e) {
              console.error('Error modifying key URL in frag parsing:', e)
            }
          }
        })

        // Intercept level loading to modify key URLs for public routes
        hls.on(HlsClass.Events.LEVEL_LOADED, (event, data) => {
          // Modify key URLs in the level data to include session slug
          if (!token && sessionSlug && data.details) {
            // Modify key URLs in level keys
            if (data.details.levelkeys) {
              Object.keys(data.details.levelkeys).forEach((keyId) => {
                const levelKey = data.details.levelkeys[keyId]
                if (levelKey?.uri && levelKey.uri.includes('/hls/key')) {
                  try {
                    const keyUrl = new URL(levelKey.uri)
                    keyUrl.searchParams.set('slug', sessionSlug)
                    if (shortKey) {
                      keyUrl.searchParams.set('shortKey', shortKey)
                    }
                    levelKey.uri = keyUrl.toString()
                  } catch (e) {
                    console.error('Error modifying level key URL:', e)
                  }
                }
              })
            }
            
            // Modify key URLs in fragments
            if (data.details.fragments) {
              data.details.fragments.forEach((frag) => {
                if (frag.decryptdata?.uri && frag.decryptdata.uri.includes('/hls/key')) {
                  try {
                    const keyUrl = new URL(frag.decryptdata.uri)
                    keyUrl.searchParams.set('slug', sessionSlug)
                    if (shortKey) {
                      keyUrl.searchParams.set('shortKey', shortKey)
                    }
                    frag.decryptdata.uri = keyUrl.toString()
                  } catch (e) {
                    console.error('Error modifying fragment key URL:', e)
                  }
                }
              })
            }
          }
        })

        hls.on(HlsClass.Events.MANIFEST_PARSED, () => {
          setHasError(false)
          if (isPlaying) {
            audioRef.current?.play().catch((e) => {
              console.error('Play error:', e)
              setHasError(true)
            })
          }
        })

        hls.on(HlsClass.Events.ERROR, (event, data) => {
          // Handle key loading errors (401 unauthorized)
          if (data.type === HlsClass.ErrorTypes.NETWORK_ERROR && 
              (data.details === HlsClass.ErrorDetails.KEY_LOAD_ERROR || 
               data.url?.includes('/hls/key'))) {
            setHasError(true)
            setErrorMessage('Authentication required for audio playback')
            setIsPlaying(false)
            return
          }
          
          if (data.fatal) {
            setHasError(true)
            setErrorMessage('HLS playback error')
            setIsPlaying(false)
          }
        })

        hlsRef.current = hls
      } else if (audioRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari)
        audioRef.current.src = trackUrl
        setHasError(false)
      } else {
        setHasError(true)
        setErrorMessage('HLS not supported')
      }
    }

    initHls()

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
    }
  }, [trackUrl, token, isPlaying])

  // Update volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume
    }
  }, [volume, isMuted])

  const handlePlayPause = async () => {
    if (!trackUrl) {
      // Try to fetch URL if not available
      if (currentTrack?.mediaId && !isLoading) {
        setIsLoading(true)
        const result = await fetchSignedUrl(currentTrack.mediaId)
        setIsLoading(false)
        
        if (result?.success && result?.signedUrl) {
          setTrackUrl(result.signedUrl)
          setHasError(false)
          // Will auto-play after URL is set
          setIsPlaying(true)
          return
        } else {
          setHasError(true)
          setErrorMessage(result?.error || 'Unable to load audio')
          return
        }
      }
      return
    }

    if (isPlaying) {
      audioRef.current?.pause()
      setIsPlaying(false)
    } else {
      setIsLoading(true)
      try {
        await audioRef.current?.play()
        setIsPlaying(true)
        setHasError(false)
      } catch (error) {
        setHasError(true)
        setErrorMessage('Playback failed')
      }
      setIsLoading(false)
    }
  }

  const handlePrevious = () => {
    setIsPlaying(false)
    setCurrentTrackIndex(prev => 
      prev === 0 ? tracks.length - 1 : prev - 1
    )
  }

  const handleNext = () => {
    setIsPlaying(false)
    setCurrentTrackIndex(prev => 
      prev === tracks.length - 1 ? 0 : prev + 1
    )
  }

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
    }
  }

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration)
    }
  }

  const handleSeek = (_, value) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value
      setCurrentTime(value)
    }
  }

  const handleEnded = () => {
    handleNext()
    // Auto-play next track
    setTimeout(() => {
      if (trackUrl) {
        setIsPlaying(true)
      }
    }, 500)
  }

  const handleAudioError = () => {
    setHasError(true)
    setErrorMessage('Audio playback error')
    setIsPlaying(false)
  }

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const VolumeIcon = isMuted || volume === 0 
    ? VolumeMuteIcon 
    : volume < 0.5 
      ? VolumeDownIcon 
      : VolumeUpIcon

  const canPlay = trackUrl && !hasError

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        padding: 3,
        minHeight: '280px',
      }}
    >
      {/* Hidden audio element with HLS.js for authenticated streaming */}
      {trackUrl && (
        <audio
          ref={audioRef}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
          onError={handleAudioError}
          style={{ display: 'none' }}
        />
      )}

      {/* Album art / visualization */}
      <Box
        sx={{
          width: '120px',
          height: '120px',
          borderRadius: '20px',
          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #3b82f6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated rings when playing */}
        {isPlaying && (
          <>
            <Box
              sx={{
                position: 'absolute',
                width: '150%',
                height: '150%',
                borderRadius: '50%',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(0.8)', opacity: 0.5 },
                  '50%': { transform: 'scale(1)', opacity: 0.2 },
                },
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                width: '120%',
                height: '120%',
                borderRadius: '50%',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                animation: 'pulse 2s ease-in-out infinite 0.5s',
              }}
            />
          </>
        )}
        <MusicNoteIcon 
          sx={{ 
            fontSize: '48px', 
            color: '#ffffff',
            animation: isPlaying ? 'bounce 1s ease-in-out infinite' : 'none',
            '@keyframes bounce': {
              '0%, 100%': { transform: 'translateY(0)' },
              '50%': { transform: 'translateY(-5px)' },
            },
          }} 
        />
      </Box>

      {/* Track info */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#374151',
            mb: 0.5,
          }}
        >
          {currentTrack.name}
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            color: hasError ? '#ef4444' : '#9ca3af',
          }}
        >
          {hasError ? errorMessage : 'Soothing Music'}
        </Typography>
      </Box>

      {/* Progress bar */}
      <Box sx={{ width: '100%', maxWidth: '250px' }}>
        <Slider
          value={currentTime}
          max={duration || 100}
          onChange={handleSeek}
          disabled={!canPlay}
          sx={{
            color: '#8b5cf6',
            height: 4,
            '& .MuiSlider-thumb': {
              width: 12,
              height: 12,
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 8px rgba(139, 92, 246, 0.16)',
              },
            },
            '& .MuiSlider-rail': {
              opacity: 0.3,
            },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -1 }}>
          <Typography sx={{ fontSize: '10px', color: '#9ca3af' }}>
            {formatTime(currentTime)}
          </Typography>
          <Typography sx={{ fontSize: '10px', color: '#9ca3af' }}>
            {formatTime(duration)}
          </Typography>
        </Box>
      </Box>

      {/* Playback controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          onClick={handlePrevious}
          disabled={tracks.length <= 1}
          sx={{ color: '#6b7280' }}
        >
          <SkipPreviousIcon />
        </IconButton>

        <IconButton
          onClick={handlePlayPause}
          disabled={!currentTrack?.mediaId}
          sx={{
            width: '56px',
            height: '56px',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
            color: '#ffffff',
            '&:hover': {
              background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
            },
            '&:disabled': {
              background: '#e5e7eb',
              color: '#9ca3af',
            },
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                width: '24px',
                height: '24px',
                border: '2px solid rgba(255,255,255,0.3)',
                borderTop: '2px solid #ffffff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          ) : isPlaying ? (
            <PauseIcon sx={{ fontSize: '28px' }} />
          ) : (
            <PlayArrowIcon sx={{ fontSize: '28px' }} />
          )}
        </IconButton>

        <IconButton
          onClick={handleNext}
          disabled={tracks.length <= 1}
          sx={{ color: '#6b7280' }}
        >
          <SkipNextIcon />
        </IconButton>
      </Box>

      {/* Volume control */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', maxWidth: '200px' }}>
        <IconButton
          onClick={() => setIsMuted(!isMuted)}
          size="small"
          sx={{ color: '#6b7280' }}
        >
          <VolumeIcon fontSize="small" />
        </IconButton>
        <Slider
          value={isMuted ? 0 : volume}
          max={1}
          step={0.01}
          onChange={(_, value) => {
            setVolume(value)
            if (value > 0) setIsMuted(false)
          }}
          sx={{
            color: '#8b5cf6',
            height: 4,
            '& .MuiSlider-thumb': {
              width: 10,
              height: 10,
            },
          }}
        />
      </Box>

      {/* Track indicator */}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {tracks.map((_, index) => (
          <Box
            key={index}
            onClick={() => {
              setIsPlaying(false)
              setCurrentTrackIndex(index)
            }}
            sx={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: index === currentTrackIndex ? '#8b5cf6' : '#e5e7eb',
              cursor: 'pointer',
              transition: 'background 0.2s ease',
            }}
          />
        ))}
      </Box>
    </Box>
  )
}
